/* @flow */
import React, {Component} from 'react';
import {type ApolloClient, withApollo} from 'react-apollo';
import {compose} from 'redux';
import {fetchListingImages} from './hitCarouselQueryService';
import SimpleImageCarousel from '@homeaway/react-simple-image-carousel';
import {SVGDefinitions, SVGIcon} from '@homeaway/react-svg';
import {
    PHOTOCAROUSEL_LEFT_LABEL,
    PHOTOCAROUSEL_RIGHT_LABEL,
    triggerCarouselEDAPEvent,
    triggerCarouselImagePositionEdapEvent
} from '../../utils/carouselEdapUtils';
import './HitCarouselContainer.less';

import {type ListingImage} from '../Hit/Hit';

export type SimpleCarouselImage = {
    src: string,
    altText: string
};

type State = {
    images: SimpleCarouselImage[],
    hasFetchedImages: boolean,
    imageIndex: number
};

type Props = {
    client?: ApolloClient,
    detailPageUrl: string,
    edapAction: string,
    images: ListingImage[],
    listingId: string,
    onImageClick?: Function,
    totalImageCount: number
};
const NUMBER_OF_DEFAULT_IMAGES = 3;
const EMPTY_CAROUSEL_IMAGE: SimpleCarouselImage = {
    altText: '',
    src: ''
};

/**
 * SimpleImageCarousel uses a specific format {'src', 'altText'} so each ListingImage had to be converted into it first,
 * before the carousel can accept that data.
 * @param images Array of ListingImage
 * @returns an array of SimpleCarouselImage or empty array, if no images were initially provided.
 */
export const convertImagesIntoSimpleCarouselFormat = (
    images: ListingImage[]
): SimpleCarouselImage[] => {
    return images
        .filter((listingImage) => !!listingImage)
        .map((listingImage) => ({
            src: listingImage.c6_uri || '',
            altText: listingImage.altText || ''
        }));
};

/**
 * Repositioning images so that when remaining ones will be downloaded, we could have them aligned with default ones that
 * we have with the listing from the start. This will help to avoid various positioning issues when navigating image carousel
 * (such as carousel jerks back and forth on navigation etc.).
 * Before all the images are loaded making sure to put empty placeholders in their places.
 */
export const transformImages = (
    images: SimpleCarouselImage[],
    totalImageCount: number,
    indexOfLastImage: number,
    prevImages?: SimpleCarouselImage[] = []
): SimpleCarouselImage[] => {
    const imageLength = images.length;
    let transformedImages: SimpleCarouselImage[] = [];

    // If currently we're dealing with default subset of images, making sure to put empty placeholders
    // in places of remaining images to preserve order.
    if (imageLength < totalImageCount) {
        transformedImages = [...images];

        for (let i = imageLength; i < totalImageCount; i++) {
            transformedImages.push(EMPTY_CAROUSEL_IMAGE);
        }

        transformedImages.push(
            transformedImages.splice(indexOfLastImage, 1)[0]
        );

        return transformedImages;
    }

    // Now, if remaining images are downloaded - put them into our list replacing placeholders.
    const restImages = images.reduce((acc, image) => {
        if (
            prevImages.findIndex((prevImage) => prevImage.src === image.src) < 0
        ) {
            return [...acc, image];
        }
        return [...acc];
    }, []);

    transformedImages = [...prevImages];

    transformedImages.splice(indexOfLastImage, 0, ...restImages);

    return transformedImages;
};

/**
 * @homeaway/svg-defs v2.13 drastically reduced the size of the chevron icons,
 * breaking our carousel buttons. These are the definitions for the pre-2.13
 * chevrons so we can stay consistent until we find a design solution.
 */
export const HitCarouselIconDefinitions = () => (
    <SVGDefinitions>
        <symbol id="SVG_SERP_CHEVRON_LEFT__24">
            <svg
                data-id="SVG_SERP_CHEVRON_LEFT__24"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16 23L5 12 16 1"
                />
            </svg>
        </symbol>
        <symbol id="SVG_SERP_CHEVRON_RIGHT__24">
            <svg
                data-id="SVG_SERP_CHEVRON_RIGHT__24"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 23l11-11L8 1"
                />
            </svg>
        </symbol>
    </SVGDefinitions>
);

HitCarouselIconDefinitions.displayName = 'HitCarouselIconDefinitions';

export class HitCarouselContainer extends Component<Props, State> {
    convertedPropImages: SimpleCarouselImage[];
    indexOfLastImage: number;

    constructor(props: Props) {
        super(props);

        const {totalImageCount, images} = props;

        this.convertedPropImages = convertImagesIntoSimpleCarouselFormat(
            images
        );
        this.indexOfLastImage =
            totalImageCount >= NUMBER_OF_DEFAULT_IMAGES
                ? NUMBER_OF_DEFAULT_IMAGES - 1
                : totalImageCount - 1;
        this.state = {
            images: transformImages(
                this.convertedPropImages,
                totalImageCount,
                this.indexOfLastImage
            ),
            hasFetchedImages: images.length === totalImageCount,
            imageIndex: 0
        };
    }
    populateRemainingImages = () => {
        const {client, listingId, totalImageCount} = this.props;
        if (!this.state.hasFetchedImages) {
            fetchListingImages({client, listingId}).then((listingImages) => {
                if (listingImages && listingImages.length) {
                    const convertedListingImages = convertImagesIntoSimpleCarouselFormat(
                        listingImages
                    );
                    this.setState({
                        images: transformImages(
                            convertedListingImages,
                            totalImageCount,
                            this.indexOfLastImage,
                            this.convertedPropImages
                        ),
                        hasFetchedImages: true
                    });
                }
            });
        }
    };

    navigateToListing = () => {
        const {detailPageUrl, onImageClick} = this.props;
        if (onImageClick) {
            onImageClick();
        }
        // TODO: remove/refactor upon incorporating anchorHref functionality for carousels
        // see https://github.homeawaycorp.com/traveler/react-simple-image-carousel/issues/1
        // and https://github.homeawaycorp.com/traveler/react-image-carousel/issues/14
        // Why? - Back button will not close a new tab when using window.open
        window.open(detailPageUrl, '_blank');
    };

    onImageNavigate = (index: number) => {
        const {edapAction} = this.props;
        const {imageIndex: previousIndex, images} = this.state;

        this.setState({imageIndex: index});
        const imageSrc = images[index].src;

        const edapLabel =
            (previousIndex + 1 === images.length && index === 0) ||
            previousIndex + 1 === index
                ? PHOTOCAROUSEL_RIGHT_LABEL
                : PHOTOCAROUSEL_LEFT_LABEL;

        triggerCarouselEDAPEvent(edapAction, edapLabel);

        if (imageSrc) {
            triggerCarouselImagePositionEdapEvent(`${imageSrc}:${index}`);
        }
        this.populateRemainingImages();
    };

    render() {
        return (
            <SimpleImageCarousel
                images={this.state.images}
                index={this.state.imageIndex}
                transitionStyle="slide"
                transitionDuration={250}
                onNextClick={this.onImageNavigate}
                onPrevClick={this.onImageNavigate}
                onClick={this.navigateToListing}
                tabIndex={-1}
                // Lifted from simple-image-carousel, with icons replaced with
                // non-baseline SERP specials.
                //
                // TODO: Remove once this issue is fixed in
                // simple-image-carousel
                renderButtons={({setPrevRef, setNextRef}) => {
                    return [
                        <button
                            key="0"
                            className="SimpleImageCarousel__button SimpleImageCarousel__button--left"
                            data-wdio="SimpleImageCarousel__button--prev"
                            ref={setPrevRef}
                            tabIndex="-1"
                        >
                            <span className="sr-only">{'Previous Image'}</span>
                            <SVGIcon
                                className="SimpleImageCarousel__svg SimpleImageCarousel__svg--left"
                                id="SERP_CHEVRON_LEFT__24"
                            />
                        </button>,
                        <button
                            key="1"
                            className="SimpleImageCarousel__button SimpleImageCarousel__button--right"
                            data-wdio="SimpleImageCarousel__button--next"
                            ref={setNextRef}
                            tabIndex="-1"
                        >
                            <span className="sr-only">{'Next Image'}</span>
                            <SVGIcon
                                className="SimpleImageCarousel__svg SimpleImageCarousel__svg--right"
                                id="SERP_CHEVRON_RIGHT__24"
                            />
                        </button>
                    ];
                }}
            />
        );
    }
}

export default compose(withApollo)(HitCarouselContainer);
